import React, { useState } from "react";
import {
  Grid,
  Box,
  Typography,
  IconButton,
  Paper,
  Menu,
  MenuItem,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CloseIcon from "@mui/icons-material/Close";
import { API_BASE_URL } from "../../utils/api";
import axios from "axios";

const FileGrid = ({ files, currentPath, onDelete, onRename }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);

  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [fileBase, setFileBase] = useState("");
  const [fileExt, setFileExt] = useState("");

  const [previewFile, setPreviewFile] = useState(null);
  const [previewType, setPreviewType] = useState("");

  const openMenu = (event, file) => {
    setAnchorEl(event.currentTarget);
    setSelectedFile(file);
  };

  const closeMenu = () => {
    setAnchorEl(null);
  };

  const startRename = () => {
    if (!selectedFile) return;

    const parts = selectedFile.split(".");
    if (parts.length > 1) {
      setFileBase(parts.slice(0, -1).join("."));
      setFileExt("." + parts.pop());
    } else {
      setFileBase(selectedFile);
      setFileExt("");
    }

    closeMenu();
    setTimeout(() => setRenameDialogOpen(true), 50);
  };

  const handleRename = () => {
    if (fileBase.trim() && selectedFile) {
      const newFullName = fileBase.trim() + fileExt;
      onRename(selectedFile, newFullName);
    }

    setRenameDialogOpen(false);
    setSelectedFile(null);
    setFileBase("");
    setFileExt("");
  };

  const handleDownload = async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/download?path=${encodeURIComponent(
          `${currentPath}/${selectedFile}`
        )}`,
        { responseType: "blob" }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", selectedFile);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error("Download failed", err);
    } finally {
      closeMenu();
    }
  };

  const handleDelete = () => {
    onDelete(selectedFile);
    closeMenu();
  };

  const handlePreview = () => {
    const ext = selectedFile?.split(".").pop().toLowerCase();
    const fileUrl = `${API_BASE_URL}/download?path=${encodeURIComponent(
      `${currentPath}/${selectedFile}`
    )}`;

    if (["jpg", "jpeg", "png", "gif", "webp"].includes(ext)) {
      setPreviewType("image");
      setPreviewFile(fileUrl);
    } else if (ext === "pdf") {
      setPreviewType("pdf");
      setPreviewFile(fileUrl);
    } else {
      alert("Preview not supported for this file type.");
    }

    closeMenu();
  };

  const dialogPaperSx = {
    borderRadius: "18px",
    background: "rgba(255,255,255,0.96)",
    backdropFilter: "blur(10px)",
    boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
    overflow: "hidden",
  };

  const fieldSx = {
    mt: 1.25,
    "& .MuiOutlinedInput-root": {
      backgroundColor: "rgba(255,255,255,0.96)",
      borderRadius: "10px",
    },
  };

  const primaryButtonSx = {
    backgroundColor: "#2f63ad",
    color: "white",
    fontWeight: 700,
    borderRadius: "10px",
    textTransform: "none",
    px: 2.25,
    boxShadow: "none",
    "&:hover": {
      backgroundColor: "#1f4f94",
      boxShadow: "none",
    },
  };

  const secondaryButtonSx = {
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 600,
  };

  return (
    <>
      <Box sx={{ mb: 2 }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            color: "#223142",
            mb: 0.5,
          }}
        >
          Files
        </Typography>

        <Typography
          variant="body2"
          sx={{
            color: "#6b778c",
          }}
        >
          Browse and manage files in the current folder.
        </Typography>
      </Box>

      <Grid container spacing={2.5}>
        {files.map((file) => (
          <Grid item key={file} xs={12} sm={6} md={4} lg={3} xl={2}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: "16px",
                position: "relative",
                border: "1px solid rgba(47,99,173,0.08)",
                boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
                backgroundColor: "rgba(255,255,255,0.96)",
                transition: "all 0.2s ease",
                height: "100%",
                minHeight: 150,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                "&:hover": {
                  boxShadow: "0 12px 28px rgba(0,0,0,0.1)",
                  transform: "translateY(-2px)",
                  borderColor: "rgba(47,99,173,0.18)",
                },
              }}
            >
              <IconButton
                size="small"
                sx={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                  borderRadius: "10px",
                  backgroundColor: "rgba(0,0,0,0.03)",
                }}
                onClick={(e) => openMenu(e, file)}
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>

              <Box display="flex" justifyContent="center" mb={1.5}>
                <InsertDriveFileIcon sx={{ fontSize: 52, color: "#2f63ad" }} />
              </Box>

              <Typography
                variant="body2"
                align="center"
                sx={{
                  maxWidth: "100%",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  fontWeight: 600,
                  color: "#223142",
                  wordBreak: "break-word",
                  overflowWrap: "anywhere",
                }}
              >
                {file}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={closeMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            borderRadius: "12px",
            boxShadow: "0 14px 30px rgba(0,0,0,0.14)",
            border: "1px solid rgba(47,99,173,0.08)",
          },
        }}
      >
        <MenuItem onClick={handlePreview}>Preview</MenuItem>
        <MenuItem onClick={handleDownload}>Download</MenuItem>
        <MenuItem onClick={startRename}>Rename</MenuItem>
        <MenuItem onClick={handleDelete} sx={{ color: "#d32f2f" }}>
          Delete
        </MenuItem>
      </Menu>

      <Dialog
        open={renameDialogOpen}
        onClose={() => setRenameDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle
          sx={{
            textAlign: "center",
            fontWeight: 700,
            fontSize: "1.3rem",
            pt: 3,
            pb: 1,
            color: "#1f1f1f",
          }}
        >
          Rename File
        </DialogTitle>

        <DialogContent sx={{ px: 3, pb: 1 }}>
          <Typography
            sx={{
              mt: 0.5,
              mb: 1,
              textAlign: "center",
              color: "#666",
              fontSize: "0.95rem",
            }}
          >
            Update the file name below.
          </Typography>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <TextField
              autoFocus
              fullWidth
              label="File Name"
              variant="outlined"
              value={fileBase}
              onChange={(e) => setFileBase(e.target.value)}
              sx={fieldSx}
            />

            {fileExt && (
              <Typography
                variant="body2"
                sx={{
                  whiteSpace: "nowrap",
                  fontWeight: 600,
                  color: "#6b778c",
                  mt: 1.25,
                }}
              >
                {fileExt}
              </Typography>
            )}
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            pb: 3,
            pt: 1.5,
            justifyContent: "center",
            gap: 1.25,
          }}
        >
          <Button
            onClick={() => setRenameDialogOpen(false)}
            variant="outlined"
            sx={secondaryButtonSx}
          >
            Cancel
          </Button>
          <Button onClick={handleRename} variant="contained" sx={primaryButtonSx}>
            Rename
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!previewFile}
        onClose={() => setPreviewFile(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            fontWeight: 700,
            color: "#1f1f1f",
          }}
        >
          Preview: {selectedFile}
          <IconButton onClick={() => setPreviewFile(null)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ minHeight: "500px", p: 2 }}>
          {previewType === "image" && (
            <Box display="flex" justifyContent="center">
              <img
                src={previewFile}
                alt={selectedFile}
                style={{
                  maxWidth: "100%",
                  maxHeight: "600px",
                  borderRadius: "10px",
                }}
              />
            </Box>
          )}

          {previewType === "pdf" && (
            <iframe
              src={previewFile}
              title={selectedFile}
              width="100%"
              height="600px"
              style={{ border: "none", borderRadius: "10px" }}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default FileGrid;
